import { useState, useContext } from "react";
import { BadgesGrid } from "../components/dashboard/BadgesGrid";
import { XPProgress } from "../components/dashboard/XPProgress";
import { QRCodeRedemption } from "../components/dashboard/QRCodeRedemption";
import { ActivityTimeline } from "../components/dashboard/ActivityTimeline";
import { useBadges } from "../contexts/BadgeContext";
import { useStudentBadgeData } from "../hooks/useStudentBadgeData";
import { Trophy, Target, History, QrCode, X } from "lucide-react";
import { UserContext } from "../contexts/UserContext";
import mediaService from "../services/mediaService";

const BadgesPage = () => {
  const { badges } = useBadges();
  const { userProfile } = useContext(UserContext);

  // ⬇️ get both total XP *and* the per-badge data
  const { studentXP, studentBadges } = useStudentBadgeData();
  // selected badge image for modal preview (null = closed)
  const [selectedBadgeImage, setSelectedBadgeImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'progress' | 'history' | 'redeem'>('overview');

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: Trophy },
    { id: 'progress' as const, label: 'Progress', icon: Target },
    { id: 'history' as const, label: 'History', icon: History },
    { id: 'redeem' as const, label: 'Redeem XP', icon: QrCode },
  ];

  return (
    <div>
      <div className="max-w-6xl mx-auto px-6">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Badges & XP</h1>
          <p className="mt-2 text-gray-600">
            Track your achievements, view your progress, and redeem XP rewards
          </p>
        </header>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
          <div className="border-b border-gray-200">
            <div className="flex">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    className={`flex-1 py-4 px-6 text-center font-medium flex items-center justify-center space-x-2 ${
                      activeTab === tab.id
                        ? 'text-indigo-600 border-b-2 border-indigo-500 bg-indigo-50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* XP Progress Section */}
                <XPProgress />
                
                {/* Badge Grid */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Badges</h2>
                  <BadgesGrid studentBadges={studentBadges} />
                </div>
              </div>
            )}

            {activeTab === 'progress' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Badge Progress Details</h2>
                  <div className="mb-6">
                    <div className="text-center p-6 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg text-white">
                      <div className="text-4xl font-bold">{studentXP}</div>
                      <div className="text-lg opacity-90">Total XP Earned</div>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    {badges.map((badge) => {
                      // try to find the matching earned badge from the student profile
                      const studentBadge =
                        studentBadges?.find((b) => b.badgeId === badge.id) || null;

                      const badgeXP = studentBadge?.currentXP ?? 0;
                      const badgeLevelName = studentBadge?.currentLevel ?? null;

                      return (
                        <div
                          key={badge.id}
                          className="flex items-start p-4 rounded-lg border border-gray-200"
                        >
                          <div
                            className="w-16 h-16 flex items-center justify-center text-3xl rounded-full mr-4 flex-shrink-0"
                            style={{
                              backgroundColor: `${badge.color}20`,
                              boxShadow: `0 0 15px ${badge.color}40`,
                            }}
                          >
                            <img
                              src={Object.values(badge.levelImages)[0]}
                              alt={`${badge.name} badge`}
                              className="w-full h-full object-contain rounded-full cursor-pointer"
                              onClick={() => setSelectedBadgeImage(Object.values(badge.levelImages)[0])}
                            />
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="text-xl font-bold text-gray-800">{badge.name}</h3>
                              {badgeLevelName ? (
                                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                  Level: {badgeLevelName}
                                </span>
                              ) : null}
                            </div>

                            <div
                              className="inline-block px-2 py-1 rounded-full text-xs font-semibold mb-2"
                              style={{
                                backgroundColor: `${badge.color}40`,
                                color: "#333",
                              }}
                            >
                              {badge.category}
                            </div>
                            <p className="text-gray-600 mb-4">{badge.description}</p>

                            {/* progress per level, but using THIS badge's XP */}
                            <div className="space-y-3">
                              {Object.entries(badge.levelThresholds).map(
                                ([level, threshold]) => {
                                  const progress = Math.min((badgeXP / threshold) * 100, 100);
                                  const isUnlocked = badgeXP >= threshold;

                                  return (
                                    <div key={level} className="flex items-center space-x-3">
                                      <div className="w-16 text-sm font-medium capitalize text-gray-600">
                                        {level}
                                      </div>
                                      <div className="flex-1">
                                        <div className="flex justify-between text-sm mb-1">
                                          <span
                                            className={
                                              isUnlocked
                                                ? "text-green-600 font-semibold"
                                                : "text-gray-500"
                                            }
                                          >
                                            {threshold} XP {isUnlocked ? "✓" : ""}
                                          </span>
                                          <span className="text-gray-500">
                                            {Math.min(badgeXP, threshold)} / {threshold}
                                          </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                          <div
                                            className={`h-2 rounded-full transition-all duration-300 ${
                                              isUnlocked ? "bg-green-500" : "bg-indigo-500"
                                            }`}
                                            style={{ width: `${progress}%` }}
                                          ></div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">XP Activity History</h2>
                  <ActivityTimeline />
                </div>
              </div>
            )}

            {activeTab === 'redeem' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Redeem XP</h2>
                  <p className="text-gray-600 mb-6">
                    Scan QR codes to redeem XP points and unlock new badge levels.
                  </p>
                  <QRCodeRedemption />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Badge Levels Reference */}
      {activeTab === 'overview' && (
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Badge Levels Guide</h2>
            
            <div className="space-y-6">
              {badges.map((badge) => (
                <div key={badge.id} className="flex items-start p-4 rounded-lg border border-gray-200">
                  <div 
                    className="w-16 h-16 flex items-center justify-center text-3xl rounded-full mr-4 flex-shrink-0"
                    style={{
                      backgroundColor: `${badge.color}20`,
                      boxShadow: `0 0 15px ${badge.color}40`
                    }}
                  >
                      <img
                        src={badge.badgeImageUrl?.startsWith('https://')
                          ? badge.badgeImageUrl
                          : mediaService.getDirectUrl(userProfile?.orgId || '', String(badge.badgeImageUrl))}
                        alt={`${badge.name} badge`}
                        className="w-full h-full object-contain rounded-full cursor-pointer"
                        onClick={() => badge.badgeImageUrl && setSelectedBadgeImage(badge.badgeImageUrl?.startsWith('https://') ? badge.badgeImageUrl : mediaService.getDirectUrl(userProfile?.orgId || '', String(badge.badgeImageUrl)))}
                      />
                  </div>
                  
                  <div>
                  {/* Badge image modal */}
                  {selectedBadgeImage && (
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
                      <div className="bg-white p-4 rounded-lg relative max-w-[90vw] max-h-[90vh]">
                        <button
                          title="Close"
                          aria-label="Close badge preview"
                          onClick={() => setSelectedBadgeImage(null)}
                          className="absolute top-2 right-2 p-2 bg-white text-gray-800 rounded-full shadow-md hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <X className="w-6 h-6" />
                        </button>
                        <img
                          title="Badge Image"
                          src={selectedBadgeImage}
                          alt="Badge preview"
                          className="max-w-full max-h-[80vh] object-contain"
                        />
                      </div>
                    </div>
                  )}
                    <h3 className="text-xl font-bold text-gray-800">{badge.name}</h3>
                    <div 
                      className="inline-block px-2 py-1 rounded-full text-xs font-semibold mb-2"
                      style={{
                        backgroundColor: `${badge.color}40`,
                        color: '#333'
                      }}
                    >
                      {badge.category} • {
                        Object.keys(badge.levelThresholds)[0]
                          .charAt(0)
                          .toUpperCase() +
                        Object.keys(badge.levelThresholds)[0].slice(1)
                      }: {badge.levelThresholds[Object.keys(badge.levelThresholds)[0]]} XP
                    </div>
                    <p className="text-gray-600">{badge.description}</p>
                    
                    {/* Level breakdown */}
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(badge.levelThresholds).map(([level, threshold]) => (
                        <div key={level} className="text-center p-2 bg-gray-50 rounded">
                          <div className="text-sm font-semibold capitalize text-gray-700">{level}</div>
                          <div className="text-xs text-gray-500">{threshold} XP</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BadgesPage;